'use client';

import { type ChangeEvent, type FormEvent, useState } from 'react';
import { BusinessInquiryAPI } from '../api/api';
import { apiPost } from '../utils/utils';
import { notify } from '../utils/notify';

type BusinessFormState = {
  name: string;
  companyName: string;
  email: string;
  phone: string;
  numberOfDevices: string;
  deviceTypes: string;
  message: string;
  preferredContactMethod: string;
  website: string;
};

const initialState: BusinessFormState = {
  name: '',
  companyName: '',
  email: '',
  phone: '',
  numberOfDevices: '',
  deviceTypes: '',
  message: '',
  preferredContactMethod: '',
  website: '',
};

export default function BusinessInquiryForm({
  title = 'Request business repair support',
  intro = "We’ll respond as soon as possible with the best support options for your business.",
  light = false,
  sectionBackground,
  cardBackground,
}: {
  title?: string;
  intro?: string;
  light?: boolean;
  sectionBackground?: string;
  cardBackground?: string;
}) {
  const [formState, setFormState] = useState<BusinessFormState>(initialState);
  const [errors, setErrors] = useState<Partial<BusinessFormState>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name } = event.target;
    const value =
      name === 'phone'
        ? event.target.value.replace(/\D/g, '').slice(0, 10)
        : event.target.value;

    setFormState((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const nextErrors: Partial<BusinessFormState> = {};

    if (!formState.name.trim()) nextErrors.name = 'Full name is required';
    if (!formState.companyName.trim()) nextErrors.companyName = 'Company name is required';
    if (!formState.email.trim()) {
      nextErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.email)) {
      nextErrors.email = 'Enter a valid email address';
    }
    if (!formState.phone.trim()) {
      nextErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formState.phone)) {
      nextErrors.phone = 'Phone number must be 10 digits';
    }
    if (!formState.numberOfDevices.trim()) nextErrors.numberOfDevices = 'Please enter the number of devices';
    if (!formState.deviceTypes.trim()) nextErrors.deviceTypes = 'Please tell us the device types';
    if (!formState.message.trim()) nextErrors.message = 'Please share a few details about the repair need';
    if (!formState.preferredContactMethod.trim()) nextErrors.preferredContactMethod = 'Select a contact method';

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validate()) return;

    try {
      setIsSubmitting(true);

      await apiPost(BusinessInquiryAPI, formState);

      await notify({
        icon: 'success',
        title: 'Business inquiry sent',
        text: 'We will follow up as soon as possible with the best support options for your business.',
        confirmButtonColor: '#FF6F00',
      });

      setFormState(initialState);
      setErrors({});
    } catch (error) {
      await notify({
        icon: 'error',
        title: 'Unable to send request',
        text: 'Something went wrong. Please try again or call us directly.',
        confirmButtonColor: '#FF6F00',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resolvedSectionBackground = sectionBackground || (light ? '#FFFFFF' : '#F8FAFF');
  const resolvedCardBackground = cardBackground || (light ? '#F8FAFF' : '#FFFFFF');

  return (
    <section data-component-id="src/components/BusinessInquiryForm.tsx:121:4" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="121" data-component-end-line="301" data-component-file="BusinessInquiryForm.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22id%22%3A%22business-inquiry%22%2C%22className%22%3A%22py-24%20px-4%20sm%3Apx-6%20lg%3Apx-8%22%7D"
      id="business-inquiry"
      className="py-24 px-4 sm:px-6 lg:px-8"
      style={{ background: resolvedSectionBackground }}
    >
      <div data-component-id="src/components/BusinessInquiryForm.tsx:126:6" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="126" data-component-end-line="300" data-component-file="BusinessInquiryForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-5xl%20mx-auto%22%7D" className="max-w-5xl mx-auto">
        <div data-component-id="src/components/BusinessInquiryForm.tsx:127:8" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="127" data-component-end-line="152" data-component-file="BusinessInquiryForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-14%22%7D" className="text-center mb-14">
          <div data-component-id="src/components/BusinessInquiryForm.tsx:128:10" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="128" data-component-end-line="142" data-component-file="BusinessInquiryForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22section-tag%20mb-4%22%2C%22textContent%22%3A%22Business%20Inquiry%20Form%22%7D" className="section-tag mb-4">
            <svg data-component-id="src/components/BusinessInquiryForm.tsx:129:12" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="129" data-component-end-line="140" data-component-file="BusinessInquiryForm.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%7D"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path data-component-id="src/components/BusinessInquiryForm.tsx:139:14" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="139" data-component-end-line="139" data-component-file="BusinessInquiryForm.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D" d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
            </svg>
            Business Inquiry Form
          </div>
          <h2 data-component-id="src/components/BusinessInquiryForm.tsx:143:10" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="143" data-component-end-line="148" data-component-file="BusinessInquiryForm.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-4xl%20sm%3Atext-5xl%20font-heading%20font-extrabold%20mb-4%22%7D"
            className="text-4xl sm:text-5xl font-heading font-extrabold mb-4"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#1A1F2E', fontWeight: 800 }}
          >
            {title}
          </h2>
          <p data-component-id="src/components/BusinessInquiryForm.tsx:149:10" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="149" data-component-end-line="151" data-component-file="BusinessInquiryForm.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20max-w-3xl%20mx-auto%22%7D" className="text-lg max-w-3xl mx-auto" style={{ color: '#64748B', fontFamily: "'DM Sans', sans-serif" }}>
            {intro}
          </p>
        </div>

        <div data-component-id="src/components/BusinessInquiryForm.tsx:154:8" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="154" data-component-end-line="299" data-component-file="BusinessInquiryForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22rounded-%5B32px%5D%20p-8%20sm%3Ap-10%22%7D"
          className="rounded-[32px] p-8 sm:p-10"
          style={{
            background: resolvedCardBackground,
            border: '1px solid rgba(30, 144, 255, 0.12)',
            boxShadow: '0 24px 70px rgba(30, 144, 255, 0.08)',
          }}
        >
          <form data-component-id="src/components/BusinessInquiryForm.tsx:162:10" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="162" data-component-end-line="298" data-component-file="BusinessInquiryForm.tsx" data-component-name="form" data-component-content="%7B%22elementName%22%3A%22form%22%2C%22className%22%3A%22space-y-6%22%7D" onSubmit={handleSubmit} noValidate className="space-y-6">
            <div data-component-id="src/components/BusinessInquiryForm.tsx:163:12" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="163" data-component-end-line="192" data-component-file="BusinessInquiryForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20gap-6%20md%3Agrid-cols-2%22%7D" className="grid gap-6 md:grid-cols-2">
              <div data-component-id="src/components/BusinessInquiryForm.tsx:164:14" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="164" data-component-end-line="175" data-component-file="BusinessInquiryForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22hidden%22%7D" className="hidden" aria-hidden="true">
                <label data-component-id="src/components/BusinessInquiryForm.tsx:165:16" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="165" data-component-end-line="165" data-component-file="BusinessInquiryForm.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22textContent%22%3A%22Website%22%7D" htmlFor="website">Website</label>
                <input data-component-id="src/components/BusinessInquiryForm.tsx:166:16" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="166" data-component-end-line="174" data-component-file="BusinessInquiryForm.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22id%22%3A%22website%22%2C%22name%22%3A%22website%22%2C%22type%22%3A%22text%22%7D"
                  id="website"
                  name="website"
                  type="text"
                  value={formState.website}
                  onChange={handleChange}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>
              <FormField data-component-id="src/components/BusinessInquiryForm.tsx:176:14" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="176" data-component-end-line="183" data-component-file="BusinessInquiryForm.tsx" data-component-name="FormField" data-component-content="%7B%22elementName%22%3A%22FormField%22%2C%22name%22%3A%22name%22%7D"
                label="Full Name"
                name="name"
                value={formState.name}
                onChange={handleChange}
                error={errors.name}
                placeholder="Your full name"
              />
              <FormField data-component-id="src/components/BusinessInquiryForm.tsx:184:14" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="184" data-component-end-line="191" data-component-file="BusinessInquiryForm.tsx" data-component-name="FormField" data-component-content="%7B%22elementName%22%3A%22FormField%22%2C%22name%22%3A%22companyName%22%7D"
                label="Company Name"
                name="companyName"
                value={formState.companyName}
                onChange={handleChange}
                error={errors.companyName}
                placeholder="Company or office name"
              />
            </div>

            <div data-component-id="src/components/BusinessInquiryForm.tsx:194:12" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="194" data-component-end-line="212" data-component-file="BusinessInquiryForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20gap-6%20md%3Agrid-cols-2%22%7D" className="grid gap-6 md:grid-cols-2">
              <FormField data-component-id="src/components/BusinessInquiryForm.tsx:195:14" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="195" data-component-end-line="203" data-component-file="BusinessInquiryForm.tsx" data-component-name="FormField" data-component-content="%7B%22elementName%22%3A%22FormField%22%2C%22name%22%3A%22email%22%2C%22type%22%3A%22email%22%7D"
                label="Email"
                name="email"
                type="email"
                value={formState.email}
                onChange={handleChange}
                error={errors.email}
                placeholder="name@company.com"
              />
              <FormField data-component-id="src/components/BusinessInquiryForm.tsx:204:14" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="204" data-component-end-line="211" data-component-file="BusinessInquiryForm.tsx" data-component-name="FormField" data-component-content="%7B%22elementName%22%3A%22FormField%22%2C%22name%22%3A%22phone%22%7D"
                label="Phone Number"
                name="phone"
                value={formState.phone}
                onChange={handleChange}
                error={errors.phone}
                placeholder="+1 (226) 962-6664"
              />
            </div>

            <div data-component-id="src/components/BusinessInquiryForm.tsx:214:12" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="214" data-component-end-line="253" data-component-file="BusinessInquiryForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20gap-6%20md%3Agrid-cols-2%22%7D" className="grid gap-6 md:grid-cols-2">
              <FormField data-component-id="src/components/BusinessInquiryForm.tsx:215:14" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="215" data-component-end-line="222" data-component-file="BusinessInquiryForm.tsx" data-component-name="FormField" data-component-content="%7B%22elementName%22%3A%22FormField%22%2C%22name%22%3A%22numberOfDevices%22%7D"
                label="Number of Devices"
                name="numberOfDevices"
                value={formState.numberOfDevices}
                onChange={handleChange}
                error={errors.numberOfDevices}
                placeholder="Example: 4"
              />
              <div data-component-id="src/components/BusinessInquiryForm.tsx:223:14" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="223" data-component-end-line="252" data-component-file="BusinessInquiryForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
                <label data-component-id="src/components/BusinessInquiryForm.tsx:224:16" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="224" data-component-end-line="230" data-component-file="BusinessInquiryForm.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-semibold%20mb-2%22%2C%22textContent%22%3A%22Preferred%20Contact%20Method%22%7D"
                  htmlFor="preferredContactMethod"
                  className="block text-sm font-semibold mb-2"
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#1A1F2E' }}
                >
                  Preferred Contact Method
                </label>
                <select data-component-id="src/components/BusinessInquiryForm.tsx:231:16" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="231" data-component-end-line="248" data-component-file="BusinessInquiryForm.tsx" data-component-name="select" data-component-content="%7B%22elementName%22%3A%22select%22%2C%22id%22%3A%22preferredContactMethod%22%2C%22name%22%3A%22preferredContactMethod%22%2C%22className%22%3A%22w-full%20rounded-2xl%20px-4%20py-3%20border%20outline-none%20transition-shadow%20focus%3Ashadow-sm%22%7D"
                  id="preferredContactMethod"
                  name="preferredContactMethod"
                  value={formState.preferredContactMethod}
                  onChange={handleChange}
                  className="w-full rounded-2xl px-4 py-3 border outline-none transition-shadow focus:shadow-sm"
                  style={{
                    background: '#FFFFFF',
                    borderColor: errors.preferredContactMethod ? '#EF4444' : '#D7E2F1',
                    color: '#334155',
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >
                  <option data-component-id="src/components/BusinessInquiryForm.tsx:244:18" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="244" data-component-end-line="244" data-component-file="BusinessInquiryForm.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22%22%2C%22textContent%22%3A%22Select%20a%20contact%20method%22%7D" value="">Select a contact method</option>
                  <option data-component-id="src/components/BusinessInquiryForm.tsx:245:18" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="245" data-component-end-line="245" data-component-file="BusinessInquiryForm.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22Phone%22%2C%22textContent%22%3A%22Phone%22%7D" value="Phone">Phone</option>
                  <option data-component-id="src/components/BusinessInquiryForm.tsx:246:18" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="246" data-component-end-line="246" data-component-file="BusinessInquiryForm.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22Email%22%2C%22textContent%22%3A%22Email%22%7D" value="Email">Email</option>
                  <option data-component-id="src/components/BusinessInquiryForm.tsx:247:18" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="247" data-component-end-line="247" data-component-file="BusinessInquiryForm.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22Text%20%2F%20WhatsApp%22%2C%22textContent%22%3A%22Text%20%2F%20WhatsApp%22%7D" value="Text / WhatsApp">Text / WhatsApp</option>
                </select>
                {errors.preferredContactMethod ? (
                  <p data-component-id="src/components/BusinessInquiryForm.tsx:250:18" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="250" data-component-end-line="250" data-component-file="BusinessInquiryForm.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20mt-2%20text-red-500%22%7D" className="text-sm mt-2 text-red-500">{errors.preferredContactMethod}</p>
                ) : null}
              </div>
            </div>

            <FormField data-component-id="src/components/BusinessInquiryForm.tsx:255:12" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="255" data-component-end-line="262" data-component-file="BusinessInquiryForm.tsx" data-component-name="FormField" data-component-content="%7B%22elementName%22%3A%22FormField%22%2C%22name%22%3A%22deviceTypes%22%7D"
              label="Device Types"
              name="deviceTypes"
              value={formState.deviceTypes}
              onChange={handleChange}
              error={errors.deviceTypes}
              placeholder="iPhones, Samsung phones, tablets, iPads, laptops"
            />

            <div data-component-id="src/components/BusinessInquiryForm.tsx:264:12" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="264" data-component-end-line="288" data-component-file="BusinessInquiryForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
              <label data-component-id="src/components/BusinessInquiryForm.tsx:265:14" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="265" data-component-end-line="271" data-component-file="BusinessInquiryForm.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-semibold%20mb-2%22%2C%22textContent%22%3A%22Message%22%7D"
                htmlFor="message"
                className="block text-sm font-semibold mb-2"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#1A1F2E' }}
              >
                Message
              </label>
              <textarea data-component-id="src/components/BusinessInquiryForm.tsx:272:14" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="272" data-component-end-line="286" data-component-file="BusinessInquiryForm.tsx" data-component-name="textarea" data-component-content="%7B%22elementName%22%3A%22textarea%22%2C%22id%22%3A%22message%22%2C%22name%22%3A%22message%22%2C%22className%22%3A%22w-full%20rounded-2xl%20px-4%20py-3%20border%20outline-none%20resize-none%20transition-shadow%20focus%3Ashadow-sm%22%7D"
                id="message"
                name="message"
                value={formState.message}
                onChange={handleChange}
                rows={6}
                className="w-full rounded-2xl px-4 py-3 border outline-none resize-none transition-shadow focus:shadow-sm"
                style={{
                  background: '#FFFFFF',
                  borderColor: errors.message ? '#EF4444' : '#D7E2F1',
                  color: '#334155',
                  fontFamily: "'DM Sans', sans-serif",
                }}
                placeholder="Tell us what needs repair, how many devices are affected, and whether you need pickup and drop."
              />
              {errors.message ? <p data-component-id="src/components/BusinessInquiryForm.tsx:287:32" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="287" data-component-end-line="287" data-component-file="BusinessInquiryForm.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20mt-2%20text-red-500%22%7D" className="text-sm mt-2 text-red-500">{errors.message}</p> : null}
            </div>

            <button data-component-id="src/components/BusinessInquiryForm.tsx:290:12" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="290" data-component-end-line="297" data-component-file="BusinessInquiryForm.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22submit%22%2C%22className%22%3A%22btn-accent%20w-full%20justify-center%20text-base%20py-4%20disabled%3Aopacity-70%22%7D"
              type="submit"
              disabled={isSubmitting}
              className="btn-accent w-full justify-center text-base py-4 disabled:opacity-70"
              style={{ background: 'linear-gradient(135deg, #FF6F00, #FF9A3C)', boxShadow: '0 6px 24px rgba(255, 111, 0, 0.3)' }}
            >
              {isSubmitting ? 'Sending...' : 'Request Business Support'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

function FormField({
  label,
  name,
  value,
  onChange,
  error,
  placeholder,
  type = 'text',
}: {
  label: string;
  name: string;
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  placeholder: string;
  type?: string;
}) {
  return (
    <div data-component-id="src/components/BusinessInquiryForm.tsx:323:4" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="323" data-component-end-line="350" data-component-file="BusinessInquiryForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
      <label data-component-id="src/components/BusinessInquiryForm.tsx:324:6" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="324" data-component-end-line="330" data-component-file="BusinessInquiryForm.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-semibold%20mb-2%22%7D"
        htmlFor={name}
        className="block text-sm font-semibold mb-2"
        style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#1A1F2E' }}
      >
        {label}
      </label>
      <input data-component-id="src/components/BusinessInquiryForm.tsx:331:6" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="331" data-component-end-line="348" data-component-file="BusinessInquiryForm.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22id%22%3A%22%5Bvar%3Aname%5D%22%2C%22name%22%3A%22%5Bvar%3Aname%5D%22%2C%22type%22%3A%22%5Bvar%3Atype%5D%22%2C%22value%22%3A%22%5Bvar%3Avalue%5D%22%2C%22className%22%3A%22w-full%20rounded-2xl%20px-4%20py-3%20border%20outline-none%20transition-shadow%20focus%3Ashadow-sm%22%7D"
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        inputMode={name === 'phone' ? 'numeric' : undefined}
        pattern={name === 'phone' ? '[0-9]*' : undefined}
        maxLength={name === 'phone' ? 10 : undefined}
        className="w-full rounded-2xl px-4 py-3 border outline-none transition-shadow focus:shadow-sm"
        style={{
          background: '#FFFFFF',
          borderColor: error ? '#EF4444' : '#D7E2F1',
          color: '#334155',
          fontFamily: "'DM Sans', sans-serif",
        }}
        placeholder={placeholder}
      />
      {error ? <p data-component-id="src/components/BusinessInquiryForm.tsx:349:15" data-component-path="src/components/BusinessInquiryForm.tsx" data-component-line="349" data-component-end-line="349" data-component-file="BusinessInquiryForm.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20mt-2%20text-red-500%22%7D" className="text-sm mt-2 text-red-500">{error}</p> : null}
    </div>
  );
}
